import {when} from 'lit/directives/when.js';
import {html} from 'lit';
import {styleMap} from 'lit/directives/style-map.js';
import {FlyErrorCodes, flyErrorLabels} from '@gl/lit/common/constants/index.js';
import {AlertIntent, AlertSize} from '@gl/lit/components/base/alert/index.js';
import type {AnnouncementIntent} from '@gl/lit/components/base/announcement/index.js';

import {
  COMPONENT_TAG as TOAST_COMPONENT_TAG,
  ActionType as ToastActionType,
} from '@gl/lit/components/base/toast/index.js';

export function stageNotification(
  message: string,
  intent: AlertIntent = AlertIntent.PRIMARY
) {
  window.dispatchEvent(
    new CustomEvent(TOAST_COMPONENT_TAG, {
      detail: {
        type: ToastActionType.SAVE_NOTIFICATION,
        payload: {
          type: intent,
          content: [message],
        },
      },
    })
  );
}

export function renderNotification(
  message: string,
  intent: AlertIntent = AlertIntent.PRIMARY,
  size?: AlertSize
) {
  if (!message) return;

  window.dispatchEvent(
    new CustomEvent(TOAST_COMPONENT_TAG, {
      detail: {
        type: ToastActionType.ADD_NOTIFICATION,
        payload: {
          type: intent,
          content: [message],
          size,
        },
      },
    })
  );
}

/**
 * Creates a human readable error message, given an error code.
 */
function createErrorCodeMsg(code: FlyErrorCodes): string {
  const label = flyErrorLabels.get(code) || 'Unexpected error.';
  return `Error (${code}): ${label}`;
}

/**
 * Safely renders an error notification.
 */
export function renderErrorNotification(
  error: unknown,
  size: AlertSize = AlertSize.XS
) {
  if (!(error instanceof Error)) return;
  if (error.name === 'AbortError') return;
  if (error instanceof SyntaxError || error.name === 'SyntaxError') {
    return renderNotification(
      createErrorCodeMsg(FlyErrorCodes.AB_SYNTAX_ERROR),
      AlertIntent.DANGER,
      size
    );
  }

  renderNotification(error.message, AlertIntent.DANGER, size);
}

export function displayApiError(err: unknown, message?: string) {
  if (err instanceof Error) {
    return renderErrorNotification(err);
  }

  renderErrorNotification(new Error(errorMessageFromApiResponse(err, message)));
}

export function errorMessageFromApiResponse(
  err: unknown,
  contextMessage?: string
): string {
  let message: string;

  const errorResponse = err as {
    message?: string;
    data?: {errors?: string[]};
    errors?: string[];
    error?: string;
  };

  if (errorResponse.message) {
    message = errorResponse.message;
  } else if (errorResponse.data?.errors) {
    message = errorResponse.data.errors.join('\n');
  } else if (errorResponse.errors) {
    message = errorResponse.errors.join('\n');
  } else if (errorResponse.error) {
    message = errorResponse.error;
  } else if (typeof err === 'string') {
    message = err;
  } else {
    message = 'An unexpected error occurred.';
  }

  return `${contextMessage ?? ''}${message}`;
}

export function renderMessageBox(
  icon: string,
  message: string,
  intent: AnnouncementIntent,
  loading?: boolean | false
) {
  return html`<fly-announcement fixed intent=${intent}>
    <div class="message-box-row">
      <div class="message-box-row__icon">
        ${when(
          loading,
          () =>
            html`<fly-loader
              ?loading=${true}
              style=${styleMap({
                '--fly-loader-size': '12px',
              })}
            >
            </fly-loader>`,
          () => html`<fly-icon icon=${icon}></fly-icon>`
        )}
      </div>
      <div class="message-box-row__text">${message}</div>
    </div>
  </fly-announcement>`;
}
